import React from "react";
import {
  Truck,
  Wrench,
  ClipboardList,
  Tractor,
  ShieldCheck,
  BadgeCheck,
  Zap,
  Smile,
  Award,
  MapPin,
  Phone,
  Mail,
  MessageCircle,
} from "lucide-react";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { FloatingContact } from "../components/FloatingContact";
import { QuoteForm } from "../components/QuoteForm";
import { CoverageMap } from "../components/CoverageMap";
import { TestimonialWall } from "../components/TestimonialWall";
import { COVERAGE_AREAS, SERVICES, IMAGES, CONTACT } from "../lib/brand";

const ICON_MAP = { Truck, Wrench, ClipboardList, Tractor };

const ServiceCard = ({ service, idx }) => {
  const Icon = ICON_MAP[service.icon] || Truck;
  return (
    <div
      className={`bg-white border border-[#cfd7db] rounded-md p-8 transition-card hover:border-[#f28a16] hover:-translate-y-1 shadow-sm hover:shadow-lg fade-up fade-up-delay-${idx + 1}`}
      data-testid={`service-card-${service.id}`}
    >
      <div className="h-12 w-12 rounded-md bg-[#f28a16] flex items-center justify-center mb-6 shadow-md">
        <Icon size={22} className="text-white" />
      </div>
      <h3 className="font-display font-bold uppercase text-2xl mb-3 leading-tight text-[#111619]">
        {service.title}
      </h3>
      <p className="text-[#3f4b52] leading-relaxed">{service.description}</p>
    </div>
  );
};

const TrustBadge = ({ icon: Icon, title, body, testid }) => (
  <div className="flex gap-4 p-6 border border-[#cfd7db] rounded-md bg-white" data-testid={testid}>
    <div className="h-12 w-12 shrink-0 rounded-md bg-[#edf2f4] border border-[#cfd7db] flex items-center justify-center">
      <Icon size={22} className="text-[#f28a16]" />
    </div>
    <div>
      <div className="font-display uppercase font-bold tracking-wide text-lg text-[#111619]">{title}</div>
      <p className="text-sm text-[#3f4b52] mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);

export const WindscreensPage = () => {
  return (
    <div className="min-h-screen bg-[#f5f6f6] text-[#111619]" data-testid="windscreens-page">
      <SiteHeader variant="full" showNav={true} />

      {/* HERO */}
      <section className="relative hero-gradient">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 sm:py-28 lg:py-36">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 border border-white/25 bg-black/30 px-3 py-1 rounded-sm mb-6 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ffc438] animate-pulse" />
              <span className="font-display uppercase tracking-widest text-[11px] text-white/90">
                Mobile Windscreen Specialists
              </span>
            </div>
            <h1 className="font-display font-black uppercase leading-[0.9] text-4xl sm:text-5xl lg:text-7xl text-white drop-shadow-[0_2px_0_rgba(0,0,0,0.35)]">
              Cracked, chipped <br />
              or shattered? <br />
              <span className="text-[#ffc438]">We come to you.</span>
            </h1>
            <p className="mt-6 text-white/85 max-w-2xl text-base sm:text-lg leading-relaxed">
              Fast, friendly, professional mobile windscreen replacement and
              chip repair across Gauteng, North West, Mpumalanga, Free State,
              Western Cape and Northern Cape. SABS-approved glass.
              Insurance-approved support.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#quote"
                className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-sm px-7 h-12 rounded-md inline-flex items-center hover:brightness-110 transition shadow-lg"
                data-testid="hero-cta-quote"
              >
                Request a Quote
              </a>
              <a
                href={`https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/40 bg-white/10 backdrop-blur-sm text-white font-display uppercase tracking-widest text-sm px-7 h-12 rounded-md inline-flex items-center hover:bg-white/20 hover:border-[#ffc438] hover:text-[#ffc438] transition"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle size={16} className="mr-2" /> WhatsApp
              </a>
              <a
                href={`tel:${CONTACT.phone}`}
                className="border border-white/40 text-white font-display uppercase tracking-widest text-sm px-7 h-12 rounded-md inline-flex items-center hover:border-[#ffc438] hover:text-[#ffc438] transition"
                data-testid="hero-cta-call"
              >
                <Phone size={16} className="mr-2" /> Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="bg-white border-b border-[#cfd7db]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Zap, label: "Fast Response" },
            { icon: Smile, label: "Friendly Service" },
            { icon: Award, label: "Professional Team" },
            { icon: ShieldCheck, label: "Insurance Approved" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3" data-testid={`strip-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon size={20} className="text-[#f28a16]" />
              <span className="font-display uppercase tracking-widest text-sm text-[#111619]">
                {label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 sm:py-28">
        <div className="max-w-2xl">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-3">
            What we do
          </div>
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#111619]">
            Specialist windscreen <br /> services. Mobile-first.
          </h2>
          <p className="text-[#3f4b52] mt-5 max-w-xl leading-relaxed">
            From a single chip on the highway to a full fleet survey, we handle
            it on-site — no workshop visit needed.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} idx={i} />
          ))}
        </div>
      </section>

      {/* QUALITY / TRUST */}
      <section className="soft-bg border-y border-[#cfd7db]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-3">
              Quality & Approvals
            </div>
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#111619]">
              SABS-approved glass. <br />
              Insurance-approved <span className="text-[#f28a16]">support.</span>
            </h2>
            <p className="text-[#3f4b52] mt-5 leading-relaxed max-w-xl">
              We only fit glass that meets South African Bureau of Standards
              specifications. Our paperwork meets insurer requirements — so
              your claim moves fast.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              <TrustBadge
                icon={ShieldCheck}
                title="SABS-Approved Glass"
                body="OEM-spec safety glass that meets SA standards for strength and clarity."
                testid="trust-sabs"
              />
              <TrustBadge
                icon={BadgeCheck}
                title="Insurance-Approved"
                body="We work directly with major insurers. Excess and approvals handled."
                testid="trust-insurance"
              />
              <TrustBadge
                icon={Zap}
                title="Mobile Fitment"
                body="On-site at home, work or roadside — wherever you are in our coverage area."
                testid="trust-mobile"
              />
              <TrustBadge
                icon={Award}
                title="Professional Team"
                body="Trained fitters, calibrated tools and clean workmanship — every job."
                testid="trust-professional"
              />
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 diagonal-stripes rounded-md" />
            <img
              src={IMAGES.windscreenInstall}
              alt="Diesel & Dust technician installing a vehicle windscreen"
              className="relative w-full h-[480px] object-cover rounded-md border border-[#cfd7db] shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section id="coverage" className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-3">
              Coverage Areas
            </div>
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#111619]">
              Six provinces. <br />
              One <span className="text-[#f28a16]">team.</span>
            </h2>
            <p className="text-[#3f4b52] mt-5 leading-relaxed">
              Our mobile units cover most of South Africa. If you're inside one
              of these provinces, we'll dispatch a fitter to your location.
            </p>

            <ul className="mt-8 space-y-3">
              {COVERAGE_AREAS.map((area) => (
                <li
                  key={area.code}
                  className="flex items-start gap-3 border-l-2 border-[#f28a16] pl-4 py-2"
                  data-testid={`coverage-${area.code.toLowerCase()}`}
                >
                  <MapPin size={18} className="text-[#f28a16] shrink-0 mt-1" />
                  <div>
                    <div className="font-display uppercase tracking-wide text-lg text-[#111619]">{area.name}</div>
                    <div className="text-xs text-[#3f4b52]">{area.note}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="relative bg-white border border-[#cfd7db] rounded-md p-6 sm:p-8 shadow-sm">
              <CoverageMap />
              <div className="absolute top-3 right-3 font-display uppercase tracking-widest text-xs text-white bg-[#f28a16] px-2 py-1 rounded-sm shadow">
                Active Regions
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-display uppercase tracking-widest text-[#3f4b52]">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm bg-[#f28a16]" />
                  <span>Covered area</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm bg-[#9aa6ad]" />
                  <span>Not currently covered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="dark-panel relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{
               background:
                 "radial-gradient(circle at 88% 12%, rgba(255,196,56,0.25), transparent 25%)",
             }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-20 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="font-display uppercase tracking-widest text-xs text-[#ffc438] mb-3">
              Online Quote Request
            </div>
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
              Tell us about <br />
              the <span className="text-[#ffc438]">damage.</span>
            </h2>
            <p className="text-white/80 mt-5 leading-relaxed">
              Fill in the form — we'll respond with a quote and the next steps.
              For urgent jobs, WhatsApp or call us directly.
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>

      {/* DIRECT CONTACT */}
      <section className="soft-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 sm:py-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          <a
            href={`tel:${CONTACT.phone}`}
            data-testid="contact-call-card"
            className="border border-[#cfd7db] rounded-md p-8 hover:border-[#f28a16] transition-card bg-white shadow-sm hover:shadow-lg group"
          >
            <Phone size={22} className="text-[#f28a16] mb-4" />
            <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mb-2">Call us</div>
            <div className="font-display text-2xl font-bold text-[#111619] group-hover:text-[#f28a16] transition">
              {CONTACT.phoneDisplay}
            </div>
          </a>
          <a
            href={`https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-card"
            className="border border-[#cfd7db] rounded-md p-8 hover:border-[#f28a16] transition-card bg-white shadow-sm hover:shadow-lg group"
          >
            <MessageCircle size={22} className="text-[#f28a16] mb-4" />
            <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mb-2">WhatsApp</div>
            <div className="font-display text-2xl font-bold text-[#111619] group-hover:text-[#f28a16] transition">
              {CONTACT.whatsappDisplay}
            </div>
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="contact-email-card"
            className="border border-[#cfd7db] rounded-md p-8 hover:border-[#f28a16] transition-card bg-white shadow-sm hover:shadow-lg group"
          >
            <Mail size={22} className="text-[#f28a16] mb-4" />
            <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mb-2">Email</div>
            <div className="font-display text-xl sm:text-2xl font-bold text-[#111619] group-hover:text-[#f28a16] transition break-all">
              {CONTACT.email}
            </div>
          </a>
        </div>
      </section>

      <TestimonialWall />

      <SiteFooter />
      <FloatingContact />
    </div>
  );
};

export default WindscreensPage;
